<template>
  <div class="car-calendaer-cell" :class="cellClass">
    <div class="day-label">{{day}}</div>
    <div class="day-body" :class="bodyClass">
      <a-button
        v-for="(item, index) in value.allows"
        :key="index"
        class="day-allow"
        type="button"
        :disabled="value.status === 'close'"
        @click="onSelected(item)"
      >
        <!-- mobile -->
        <template v-if="breakpoint <= BREAKPOINT_SM">
          <svg viewBox="0 0 24 4" width="24" height="4">
            <g v-if="!item.begin.here">
              <circle cx="6" cy="2" r="1"/>
              <circle cx="12" cy="2" r="2"/>
              <circle cx="18" cy="2" r="1"/>
            </g>
          </svg>

          <template v-if="value.status === 'close'">
            <span class="time-closed">定休日</span>
          </template>
          <template v-else>
            <span class="time-begin" :class="{em: item.begin.em}">{{(!item.begin.em && item.begin.time === item.end.time) ? '\u3000' : item.begin.time}}</span>
            <span class="time-to"/>
            <span class="time-end" :class="{em: item.end.em}">{{(!item.end.em && item.begin.time === item.end.time) ? '\u3000' : item.end.time}}</span>
          </template>

          <svg viewBox="0 0 24 4" width="24" height="4">
            <g v-if="!item.end.here">
              <circle cx="6" cy="2" r="1"/>
              <circle cx="12" cy="2" r="2"/>
              <circle cx="18" cy="2" r="1"/>
            </g>
          </svg>
        </template>

        <!-- >= tablet -->
        <template v-else>
          <svg viewBox="0 0 8 24" width="8" height="24">
            <g v-if="!item.begin.here">
              <rect x="0" y="0" width="8" height="24"/>
            </g>
            <g v-else>
              <path d="M0 12 l8 -12 v24 z"/>
            </g>
          </svg>

          <template v-if="value.status === 'close'">
            <span class="time-closed">定休日</span>
          </template>
          <template v-else>
            <span class="time-begin" :class="{em: item.begin.em}">{{(!item.begin.em && item.begin.time === item.end.time) ? '\u3000' : item.begin.time}}</span>
            <span class="time-to"/>
            <span class="time-end" :class="{em: item.end.em}">{{(!item.end.em && item.begin.time === item.end.time) ? '\u3000' : item.end.time}}</span>
          </template>

          <svg viewBox="0 0 8 24" width="8" height="24">
            <g v-if="!item.end.here">
              <rect x="0" y="0" width="8" height="24"/>
            </g>
            <g v-else>
              <path d="M8 12 l-8 12 v-24 z"/>
            </g>
          </svg>
        </template>
      </a-button>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/libs/vuetify/styles/variables.scss';

.specialcar-calendar .car-calendaer-cell {
  $day-body-min-height: 4rem;

  .day-label {
    @extend %typo-body-2;

    padding: .5rem 1rem;
    text-align: right;
    color: $caption-black-text-color;
  }

  &.is-saturday {
    .day-label {
      color: #1565C0 !important;
      font-weight: 700;
    }
  }

  &.is-holiday {
    .day-label {
      color: #F44336 !important;
      font-weight: 700;
    }
  }

  &.is-closed {
    background-color: #66BB6A;

    .day-label {
      color: white;
      font-weight: 700;
    }
  }

  .day-body {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: $day-body-min-height;
  }

  .day-body.is-busy {
    color: $error-color;

    &::before {
      content: '予約済';
    }
  }

  .day-allow {
    @extend %typo-body-2;
    line-height: 1.5rem;

    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-start;
    justify-content: center;

    > svg {
      fill: #1976D2;
    }

    > span {
      background-color: #1976D2;
      color: $title-white-text-color;
    }
  }

  .time-begin {
    margin: 0;
    padding: 0;
    flex: 1;
    text-align: right;
  }

  .time-end {
    padding: 0;
    margin: 0;
    flex: 1;
    text-align: left;
  }

  .time-closed {
    margin: 0;
    padding: 0;
    flex: 1;
    text-align: center;
  }

  .time-to {
    padding: 0 .125rem;
  }

  .time-to::before {
    content: '〜';
  }

  .em {
    font-weight: bold;
  }
}

.specialcar-calendar.is-mobile .car-calendaer-cell {
  $day-body-min-height: 5rem;

  .day-label {
    padding: .5rem 0;
    text-align: center;
  }

  .day-body {
    min-height: $day-body-min-height;
  }

  .day-body.is-busy::before {
    content: '✕';
  }

  .day-allow {
    @extend %typo-caption;

    flex-flow: column nowrap;
    align-items: stretch;
    justify-content: flex-start;
    line-height: 1rem;

    > svg {
      margin: 0 auto;
    }

    > span {
      text-align: center;
    }
  }

  .time-begin {
    margin: .25rem 0 0 0;
    padding: .25rem 0 0 0;
  }

  .time-end {
    padding: 0 0 .25rem 0;
    margin: 0 0 .25rem 0;
  }

  .time-closed {
    margin: .25rem 0;
    padding: 1.25rem 0;
  }

  .time-to {
    padding: 0;
  }

  .time-to::before {
    content: '↓';
  }
}
</style>

<script>
import PartBase from '@/mixins/PartBase'
import AButton from '@/components/AButton'

export default {
  name: 'CarCalendarCell',
  mixins: [
    PartBase
  ],
  components: {
    AButton
  },
  props: {
    date: {
      type: String,
      required: true
    },
    day: {
      type: Number,
      required: true
    },
    value: {
      type: Object,
      default: () => ({
        status: 'disabled',
        allows: [],
        holiday: false,
        hi: false
      })
    }
  },
  computed: {
    cellClass () {
      const classes = []
      if (this.value.status === 'close') {
        classes.push('is-closed')
      }
      if (this.value.holiday) {
        classes.push('is-holiday')
      } else if (this.value.saturday) {
        classes.push('is-saturday')
      }
      return classes
    },
    bodyClass () {
      if (this.value.status === 'busy') {
        return ['is-busy']
      }
      return []
    }
  },
  methods: {
    onSelected (item) {
      this.$emit('selected', this.date, item)
    }
  }
}
</script>
